import { useMemo } from "react";
import { matchPath } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Roles } from "@/shared/utils/constants";
import { PrivateRoutes } from "@/router/Routes";
import { MenuItem } from "@/components_v2/Menu/types";
import { FullUserResponse } from "@/redux/users/types";

import {
  House,
  FileStack,
  Send,
  Paperclip,
  Building2,
  FileText,
  ScreenShare,
  Users,
  Folder,
  LayoutDashboard,
} from "lucide-react";

const useMenuItems = (currentUser: FullUserResponse | null) => {
  const { t } = useTranslation();

  return useMemo(() => {
    if (!currentUser) return [];

    const items: MenuItem[] = [
      {
        icon: <House size={20} />,
        key: "/",
        label: t("layouts.menu.home"),
      },
      {
        icon: <LayoutDashboard size={20} />,
        key: "/dashboard",
        label: t("layouts.menu.dashboard"),
      },
      {
        icon: <FileStack size={20} />,
        key: "/evidence",
        label: t("layouts.menu.evidence"),
        children: [
          {
            key: "/evidence/invites",
            icon: <Send size={20} />,
            label: t("layouts.menu.invites"),
          },
          {
            key: "/files",
            icon: <Paperclip size={20} />,
            label: t("layouts.menu.files"),
          },
        ],
      },
      ...(currentUser.organization
        ? [
            {
              icon: <Building2 size={20} />,
              key: `/signatories/${currentUser.organization.id}`,
              label: t("layouts.menu.organization"),
            },
          ]
        : []),
      {
        icon: <FileText size={20} />,
        key: "/reports",
        label: t("layouts.menu.reports"),
        children: [
          {
            key: "/reports/views",
            icon: <ScreenShare size={20} />,
            label: t("layouts.menu.publishAndShare"),
          },
        ],
      },
      {
        icon: <Users size={20} />,
        key: "/community",
        label: t("layouts.menu.community"),
        children: [
          {
            icon: <Folder size={20} />,
            key: "/directory",
            label: t("layouts.menu.directory"),
          },
        ],
      },
    ];

    return items.filter((item) => {
      if (!currentUser) return false;

      const route = PrivateRoutes.find((route) => {
        return matchPath({ path: route.path }, item.key) !== null;
      });
      if (!route || !route.allowedRoles) return false;

      if (currentUser.role === Roles.ADMIN.value) {
        return !(
          !route.allowedRoles || !route.allowedRoles.includes(Roles.ADMIN.value)
        );
      }

      if (currentUser.role === Roles.USER.value) {
        const hasAllowedUserRole =
          route.allowedRoles && route.allowedRoles.includes(Roles.USER.value);

        if (!hasAllowedUserRole) {
          return false;
        }
        return (
          currentUser.organizationRole &&
          route.allowedOrgRoles.includes(currentUser.organizationRole)
        );
      }
    });
  }, [currentUser]);
};

export default useMenuItems;
